import React, { ReactNode, useState } from "react";
import { Box, Button, Popover, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FaArrowUpShortWide } from "react-icons/fa6";
import { FaArrowDownWideShort } from "react-icons/fa6";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';

export type DefaultOrderValue = "asc" | "desc";

interface ListOption<T> {
  icon?: ReactNode;
  label: string;
  value: T;
}

interface SortButtonProps {
  initialOrder?: DefaultOrderValue;
  initialSort: string;
  onChange(sortBy: string, order: DefaultOrderValue): void;
  orderOptions?: Array<ListOption<DefaultOrderValue>>;
  sortByOptions: Array<ListOption<string>>;
}

export default function SortButton(props: SortButtonProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const defaultOrderOptions: Array<ListOption<DefaultOrderValue>> = [{
    icon: <FaArrowUpShortWide />,
    label: i18n.t("ascending"),
    value: "asc"
  }, {
    icon: <FaArrowDownWideShort />,
    label: i18n.t("descending"),
    value: "desc"
  }];

  const [order, setOrder] = useState<DefaultOrderValue>(props.initialOrder || defaultOrderOptions[0].value);
  const [sortBy, setSortBy] = useState<string>(props.initialSort);

  const orderOptions = props.orderOptions || defaultOrderOptions;

  function handleSortByChange(value: string) {
    setSortBy(value);
    props.onChange(value, order);
  }

  function handleOrderChange(value: DefaultOrderValue) {
    setOrder(value);
    props.onChange(sortBy, value);
  }

  const open = Boolean(anchorEl);
  const hasOrderIcon = orderOptions.find((option) => !!option.icon);
  const hasSortByIcon = props.sortByOptions.find((option) => !!option.icon);
  
  return (
    <Box>
      <Button size="small" variant="contained" onClick={(event) => setAnchorEl(event.currentTarget)}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1
          }}>
          {order === "asc" && <FaArrowUpShortWide fontSize={24}/>}
          {order === "desc" && <FaArrowDownWideShort fontSize={24}/>}
          <Box>Sort</Box>
          <ArrowDropDownIcon/>
        </Stack>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{
          horizontal: "right",
          vertical: "top"
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom'
        }}
      >
        <Stack
          sx={{
            minWidth: 150,
            paddingTop: 1,
            paddingBottom: 1
          }}>
          {props.sortByOptions.length > 0 &&
            <>
              <Typography
                variant="body1"
                sx={{
                  paddingRight: 2,
                  paddingLeft: 2
                }}>{i18n.t("sort-button.sort-by-label")}</Typography>
              {props.sortByOptions.map((option, index) => {
                return (
                  <Box
                    key={`SortButton.Order.${index}`}
                    sx={{
                      paddingRight: 1,
                      paddingLeft: 1
                    }}>
                    <Button fullWidth onClick={() => handleSortByChange(option.value)} size="small" sx={{justifyContent: "start", paddingTop: 0.5, paddingRight: 1, paddingBottom: 0.5, paddingLeft: 1}}>
                      <Stack direction="row" sx={{
                        alignItems: "center"
                      }}>
                        <Box
                          sx={{
                            display: "flex",
                            marginRight: 1,
                            alignItems: "center"
                          }}>
                          <CheckIcon sx={{visibility: sortBy === option.value ? "visible" : "hidden", fontSize: 16}} />
                        </Box>
                        {hasSortByIcon &&
                          <Box
                            sx={{
                              display: "flex",
                              marginRight: 0.5,
                              minWidth: 16,
                              alignItems: "center"
                            }}>
                            {option.icon && option.icon}
                          </Box>
                        }
                        <Typography>{option.label}</Typography>
                      </Stack>
                    </Button>
                  </Box>
                );
              })}
            </>
          }
          {orderOptions.length > 0 &&
            <>
              <Typography
                variant="body1"
                sx={{
                  paddingRight: 2,
                  paddingLeft: 2
                }}>{i18n.t("sort-button.order-label")}</Typography>
              {orderOptions.map((option, index) => {
                return (
                  <Box
                    key={`SortButton.Order.${index}`}
                    sx={{
                      paddingRight: 1,
                      paddingLeft: 1
                    }}>
                    <Button fullWidth onClick={() => handleOrderChange(option.value)} size="small" sx={{justifyContent: "start", paddingTop: 0.5, paddingRight: 1, paddingBottom: 0.5, paddingLeft: 1}}>
                      <Stack direction="row" sx={{
                        alignItems: "center"
                      }}>
                        <Box
                          sx={{
                            display: "flex",
                            marginRight: 1,
                            minWidth: 16,
                            alignItems: "center"
                          }}>
                          <CheckIcon sx={{display: order === option.value ? "unset" : "none", fontSize: 16}} />
                        </Box>
                        {hasOrderIcon &&
                          <Box
                            sx={{
                              display: "flex",
                              marginRight: 0.5,
                              minWidth: 16,
                              alignItems: "center"
                            }}>
                            {option.icon && option.icon}
                          </Box>
                        }
                        <Typography>{option.label}</Typography>
                      </Stack>
                    </Button>
                  </Box>
                );
              })}
            </>
          }
        </Stack>
      </Popover>
    </Box>
  );
}